<template>
  <div class="audio-player-modal" v-if="visible">
    <div class="modal-overlay" @click="closeModal"></div>
    <div class="modal-container">
      <div class="modal-header">
        <div class="robot-info">
          <div class="robot-avatar">
            <img :src="robotInfo.avatar || defaultAvatar" alt="robot avatar">
          </div>
          <div class="robot-details">
            <div class="robot-name">{{ robotInfo.name }}</div>
            <div class="robot-serial">{{ robotInfo.serialNumber }}</div>
          </div>
        </div>
        <div class="close-btn" @click="closeModal" :title="$translate('close')">
          <svg-icon name="close" class="close-icon" />
        </div>
      </div>
      
      <div class="modal-content">
        <div class="content-header">
          <div class="title">{{ $translate('songList') }}</div>
          <div class="sync-btn" @click="syncAudioFiles" :title="$translate('refresh')" v-if="!loading">
            <svg-icon name="sync" class="sync-icon" :class="{ 'rotating': syncing }" />
          </div>
        </div>
        
        <!-- 加载状态 -->
        <div class="loading-container" v-if="loading">
          <div class="loading-spinner"></div>
          <div class="loading-text">{{ $translate('loading') }}...</div>
        </div>
        
        <!-- 音频文件列表 -->
        <div class="audio-list" v-else-if="audioList.length > 0">
          <div 
            class="audio-item"
            v-for="(audio, index) in audioList"
            :key="index"
            :class="{
              'playing': currentPlaying === audio.fileName && isPlaying,
              'preparing': currentPlaying === audio.fileName && isPreparing,
              'checking': currentPlaying === audio.fileName && isChecking
            }"
          >
            <div class="audio-info">
              <div class="audio-name" :title="audio.fileName">{{ audio.fileName }}</div>
              <div class="audio-size" v-if="audio.fileSize">{{ formatFileSize(audio.fileSize) }}</div>
            </div>
            
            <div class="audio-controls">
              <div 
                class="play-btn"
                @click="togglePlay(audio)"
                :class="{
                  'playing': currentPlaying === audio.fileName && isPlaying,
                  'preparing': currentPlaying === audio.fileName && isPreparing,
                  'checking': currentPlaying === audio.fileName && isChecking
                }"
                :title="getPlayButtonTitle(audio)"
              >
                <!-- 检查对讲状态时显示加载动画 -->
                <div class="loading-animation" v-if="currentPlaying === audio.fileName && isChecking">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <!-- 准备中状态显示加载动画 -->
                <div class="loading-animation" v-else-if="currentPlaying === audio.fileName && isPreparing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <!-- 正常播放状态 -->
                <svg-icon
                  v-else
                  :name="currentPlaying === audio.fileName && isPlaying ? 'pause' : 'play'"
                  class="play-icon"
                />
              </div>
            </div>
          </div>
        </div>
        
        <!-- 空状态 -->
        <div class="empty-state" v-else-if="!loading">
          <div class="empty-icon">
            <svg-icon name="playlist" />
          </div>
          <div class="empty-text">{{ $translate('noAudioFiles') }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/index'
import { getMvpUrlMap, connectWithUrl, sendMsg } from '@/utils/websocket'
import { getTenantId } from "@/libs/util"

export default {
  name: 'AudioPlayerModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    robotInfo: {
      type: Object,
      required: true,
      default: () => ({
        id: null,
        name: '',
        serialNumber: '',
        avatar: ''
      })
    }
  },
  data() {
    return {
      audioList: [],
      loading: false,
      syncing: false,
      currentPlaying: null,
      isPlaying: false,
      isPreparing: false,
      isChecking: false,
      defaultAvatar: require('@/assets/images/voice/Robot-icon.png'),
      VoiceIntercomURL: getMvpUrlMap,
      checkingIntercomRobots: new Set(),
      musicSocket: null,
      tenantId: getTenantId(),
      userId: this.$store.state.user.userInfo.userId,
      playState: 1, // 播放状态：1:已停止 2:暂停中 3:做准备 4:播放中
      volume: 50,
      muted: false
    }
  },
  mounted() {
    // 初始化 WebSocket 连接
    this.initWebSocket()
  },
  beforeDestroy() {
    // 关闭 WebSocket 连接
    if (this.musicSocket) {
      this.musicSocket.close && this.musicSocket.close()
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.robotInfo.id) {
        this.loadAudioList()
        this.refreshRobotStatus()
      } else {
        this.resetState()
      }
    },
    robotInfo: {
      handler(newVal) {
        if (newVal.id && this.visible) {
          this.loadAudioList()
          this.refreshRobotStatus()
        }
      },
      deep: true
    }
  },
  methods: {
    // 初始化 WebSocket 连接
    initWebSocket() {
      connectWithUrl('/sound', "musicSocketUrl", JSON.stringify({
        tenantId: this.tenantId,
        type: `init:${this.userId}`,
        language: this.$store.state.app.language,
      }), (data) => {
        this.handleWebSocketMessage(data)
      }, (socket) => {
        this.musicSocket = socket
      })
    },
    
    // 处理 WebSocket 消息
    handleWebSocketMessage(message) {
      try {
        console.log('AudioPlayerModal WebSocket 消息:', message)
        const data = typeof message === 'string' ? JSON.parse(message) : message
        
        // 只处理当前机器人的消息
        if (data.robotId !== this.robotInfo.id) {
          return
        }
        
        switch(data.type) {
          case 'speaker_volume':
            this.updateSpeakerVolume(data)
            break
          case 'play_status':
            this.updatePlayStatus(data)
            break
          case 'cmd_resp_play_command_success':
          case 'cmd_resp_pause_or_resume_success':
          case 'cmd_resp_set_volume_success':
          case 'cmd_resp_toggle_mute_success':
            // 刷新状态
            this.refreshRobotStatus()
            break
          case 'cmd_resp_play_command_error':
          case 'cmd_resp_pause_or_resume_error':
            // 播放失败，重置状态
            this.isPreparing = false
            this.currentPlaying = null
            this.$Notice.error({
              title: this.$translate('error'),
              desc: data.data || this.$translate('playFailed')
            })
            break
          default:
            console.log('未处理的 WebSocket 消息类型:', data.type)
        }
      } catch (error) {
        console.error('处理 WebSocket 消息出错:', error)
      }
    },
    
    // 更新扬声器音量数据
    updateSpeakerVolume(data) {
      if (data.robotId !== this.robotInfo.id) return
      
      this.volume = data.data.volume
      this.muted = data.data.muted
    },
    
    // 更新播放状态
    updatePlayStatus(data) {
      if (data.robotId !== this.robotInfo.id) return
      
      this.playState = data.data.playState
      // 更新播放状态: 4表示播放中
      this.isPlaying = data.data.playState === 4
      this.isPreparing = data.data.playState === 3
      
      // 更新当前播放歌曲
      if (data.data.filename) {
        this.currentPlaying = data.data.filename
      }
      
      // 如果停止了，清除当前播放
      if (data.data.playState === 1) {
        this.currentPlaying = null
        this.isPlaying = false
        this.isPreparing = false
      }
    },
    
    // 刷新机器人状态
    refreshRobotStatus() {
      if (!this.robotInfo.id) return
      
      sendMsg("musicSocketUrl", JSON.stringify({
        tenantId: this.tenantId,
        robotId: this.robotInfo.id,
        language: this.$store.state.app.language,
        type: 'refresh_volume_status'
      }))
    },
    
    // 加载音频文件列表
    async loadAudioList() {
      if (!this.robotInfo.id) return
      
      this.loading = true
      try {
        const res = await api.getData(`notice-server/sound/all?robotId=${this.robotInfo.id}`)
        if (res.state === 1) {
          this.audioList = (res.data || []).map(item => ({
            fileName: item.fileName,
            soundId: item.soundId,
            fileSize: item.fileSize || null
          }))
        } else {
          this.$Notice.error({
            title: this.$translate('error'),
            desc: res.msg
          })
        }
      } catch (error) {
        this.$Notice.error({
          title: this.$translate('error'),
          desc: this.$translate('loadAudioListFailed')
        })
      } finally {
        this.loading = false
      }
    },
    
    // 同步音频文件
    async syncAudioFiles() {
      if (!this.robotInfo.id || this.syncing) return
      
      this.syncing = false
      this.loadAudioList()
    },
    
    // 播放/暂停音频
    async togglePlay(audio) {
      if (this.isChecking) return
      
      // 如果当前正在播放同一首歌，则暂停/恢复
      if (this.currentPlaying === audio.fileName) {
        await this.pauseOrResumeAudio()
      } else {
        // 播放新的音频文件
        await this.playAudio(audio)
      }
    },
    
    // 播放指定音频
    async playAudio(audio) {
      try {
        this.isPreparing = true
        this.currentPlaying = audio.fileName

        // 检查对讲状态
        const canProceed = await this.checkIntercomStatus(this.robotInfo.serialNumber)
        if (!canProceed) {
          this.isPreparing = false
          this.currentPlaying = null
          return
        }

        const res = await api.postData('notice-server/cmd/audio/playCommand', {
          robotId: this.robotInfo.id,
          data: {
            playCommand: 1, // 播放指定曲目
            filename: audio.fileName
          }
        })
        
        if (res.state !== 0) {
          // 播放命令发送成功，等待WebSocket更新状态
          // 不立即显示成功提示，由WebSocket状态更新后处理
        } else {
          this.isPreparing = false
          this.currentPlaying = null
          this.$Notice.error({
            title: this.$translate('error'),
            desc: res.msg
          })
        }
      } catch (error) {
        this.isPreparing = false
        this.currentPlaying = null
        this.$Notice.error({
          title: this.$translate('error'),
          desc: this.$translate('playFailed')
        })
      }
    },
    
    // 暂停/恢复播放
    async pauseOrResumeAudio() {
      try {
        const res = await api.postData('notice-server/cmd/audio/pauseOrResume', {
          robotId: this.robotInfo.id
        })
        
        if (res.state !== 0) {
          // 暂停/恢复命令发送成功，等待WebSocket更新状态
          // 不立即修改状态，由WebSocket更新
        } else {
          this.$Notice.error({
            title: this.$translate('error'),
            desc: res.msg
          })
        }
      } catch (error) {
        this.$Notice.error({
          title: this.$translate('error'),
          desc: this.$translate('pauseResumeFailed')
        })
      }
    },
    
    // 检查对讲状态
    async checkIntercomStatus(serialNumber) {
      if (!this.VoiceIntercomURL || !serialNumber) return true
      
      return new Promise(async (resolve) => {
        this.isChecking = true
        this.checkingIntercomRobots.add(serialNumber)
        
        try {
          const res = await api.getData(
            `${this.VoiceIntercomURL}/api/play/broadcast/status/${serialNumber}/${serialNumber}?linkId=${this.$store.state.user.token}`,
            this,
            true
          )
          console.log(res)
          if (res.code === 0 && res.data && res.data.inUse === false) {
            // 对讲未激活，可以播放
            resolve(true)
          } else {
            if(res.state == 0){
              this.$Notice.error({
                title: this.$translate('error'),
                desc: res.msg
              })
              return resolve(false)
            }
            // 对讲激活中，询问用户是否停止对讲
            this.$Modal.confirm({
              title: this.$translate('tip'),
              content: this.$translate('robotIntercomActiveStopToPlay'),
              okText: this.$translate('stopIntercom'),
              cancelText: this.$translate('cancel'),
              onOk: async () => {
                const stopped = await this.stopIntercom(serialNumber)
                resolve(stopped)
              },
              onCancel: () => {
                resolve(false)
              }
            })
          }
        } catch (error) {
          this.$Notice.error({
            title: this.$translate('error'),
            desc: this.$translate('checkIntercomStatusFailed')
          })
          resolve(false)
        } finally {
          this.isChecking = false
          this.checkingIntercomRobots.delete(serialNumber)
        }
      })
    },
    
    // 停止对讲
    async stopIntercom(serialNumber) {
      try {
        const res = await api.getData(
          `${this.VoiceIntercomURL}/api/play/broadcast/stop/${serialNumber}/${serialNumber}?linkId=${this.$store.state.user.token}`,
          this,
          true
        )
        
        if (res.code === 0) {
          this.$Notice.success({
            title: this.$translate('success'),
            desc: this.$translate('stopIntercomSuccess')
          })
          return true
        } else {
          this.$Notice.error({
            title: this.$translate('error'),
            desc: res.msg || this.$translate('stopIntercomFailed')
          })
          return false
        }
      } catch (error) {
        this.$Notice.error({
          title: this.$translate('error'),
          desc: this.$translate('stopIntercomFailed')
        })
        return false
      }
    },
    
    // 格式化文件大小
    formatFileSize(size) {
      if (!size) return ''
      const units = ['B', 'KB', 'MB', 'GB']
      let index = 0
      while (size >= 1024 && index < units.length - 1) {
        size /= 1024
        index++
      }
      return `${size.toFixed(1)} ${units[index]}`
    },
    
    // 获取播放按钮提示文本
    getPlayButtonTitle(audio) {
      if (this.isChecking) {
        return this.$translate('checkingIntercom')
      }
      if (this.currentPlaying === audio.fileName && this.isPreparing) {
        return this.$translate('preparing')
      }
      if (this.currentPlaying === audio.fileName && this.isPlaying) {
        return this.$translate('pause')
      }
      return this.$translate('play')
    },
    
    // 关闭弹窗
    closeModal() {
      this.$emit('close')
      this.resetState()
    },
    
    // 重置状态
    resetState() {
      this.audioList = []
      this.currentPlaying = null
      this.isPlaying = false
      this.isPreparing = false
      this.isChecking = false
      this.loading = false
      this.syncing = false
    }
  }
}
</script>

<style lang="less" scoped>
.audio-player-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
}

.modal-container {
  position: relative;
  width: 600px;
  max-width: 90vw;
  max-height: 80vh;
  background: linear-gradient(135deg,
    rgba(10, 25, 47, 0.98) 0%,
    rgba(21, 67, 126, 0.98) 100%
  );
  border-radius: 16px;
  border: 1px solid rgba(64, 169, 255, 0.3);
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3),
    0 0 30px rgba(64, 169, 255, 0.2);
  backdrop-filter: blur(20px);
  overflow: hidden;
  animation: modalShow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes modalShow {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.1),
    transparent
  );
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  .robot-info {
    display: flex;
    align-items: center;
    gap: 16px;
    
    .robot-avatar {
      width: 50px;
      height: 50px;
      border-radius: 50%;
      overflow: hidden;
      border: 2px solid rgba(64, 169, 255, 0.5);
      
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
    
    .robot-details {
      .robot-name {
        font-size: 18px;
        font-weight: bold;
        color: #ffffff;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
      }
      
      .robot-serial {
        font-size: 14px;
        color: #ffca00;
        margin-top: 4px;
        opacity: 0.9;
      }
    }
  }
  
 .close-btn {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.05);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 0;
      height: 0;
      background: rgba(0, 191, 255, 0.3);
      border-radius: 50%;
      transform: translate(-50%, -50%);
      transition: all 0.3s ease;
    }
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(0, 191, 255, 0.6);
      transform: scale(1.1) rotate(90deg);
      box-shadow: 0 0 15px rgba(0, 191, 255, 0.5);
      
      &::before {
        width: 100%;
        height: 100%;
      }
    }
    
    .close-icon {
      font-size: 25px;
      color: rgba(255, 255, 255, 0.9);
      position: relative;
      z-index: 1;
    }
  }
}

.modal-content {
  height: 500px;
  display: flex;
  flex-direction: column;
  
  .content-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px 16px;
    
    .title {
      font-size: 20px;
      font-weight: bold;
      color: #ffffff;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    }
    
    .sync-btn {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: rgba(64, 169, 255, 0.2);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.3s ease;
      
      &:hover {
        background: rgba(64, 169, 255, 0.3);
        transform: scale(1.05);
      }
      
      .sync-icon {
        font-size: 18px;
        color: rgba(64, 169, 255, 0.9);
        transition: transform 0.3s ease;
        
        &.rotating {
          animation: rotate 1s linear infinite;
        }
      }
    }
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 16px;
  
  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(64, 169, 255, 0.2);
    border-top: 3px solid rgba(64, 169, 255, 0.8);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  .loading-text {
    font-size: 16px;
    color: rgba(255, 255, 255, 0.8);
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.audio-list {
  flex: 1;
  padding: 0 24px 24px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
  
  .audio-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    margin-bottom: 12px;
    border-radius: 12px;
    background: linear-gradient(
      145deg,
      rgba(16, 32, 58, 0.7) 0%,
      rgba(13, 71, 161, 0.4) 100%
    );
    border: 1px solid rgba(64, 169, 255, 0.2);
    transition: all 0.3s ease;
    backdrop-filter: blur(5px);
    
    &:hover {
      transform: translateX(-3px);
      border-color: rgba(64, 169, 255, 0.4);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
    }
    
    &.playing {
      background: linear-gradient(
        145deg,
        rgba(13, 71, 161, 0.6) 0%,
        rgba(33, 150, 243, 0.4) 100%
      );
      border-color: rgba(64, 169, 255, 0.6);
      box-shadow: 0 6px 20px rgba(33, 150, 243, 0.2);
      
      .audio-name {
        color: #fff;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      }
    }
    
    &.preparing {
      background: linear-gradient(
        145deg,
        rgba(255, 152, 0, 0.5) 0%,
        rgba(255, 193, 7, 0.3) 100%
      );
      border-color: rgba(255, 193, 7, 0.5);
      
      .audio-name {
        color: #fff;
      }
    }
    
    &.checking {
      background: linear-gradient(
        145deg,
        rgba(255, 152, 0, 0.6) 0%,
        rgba(255, 193, 7, 0.4) 100%
      );
      border-color: rgba(255, 193, 7, 0.6);
    }
    
    .audio-info {
      flex: 1;
      
      .audio-name {
        font-size: 15px;
        color: rgba(255, 255, 255, 0.9);
        margin-bottom: 4px;
        max-width: 400px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        transition: all 0.3s ease;
      }
      
      .audio-size {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.6);
      }
    }
    
    .audio-controls {
      .play-btn {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        background: rgba(64, 169, 255, 0.2);
        border: 1px solid rgba(64, 169, 255, 0.4);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s ease;
        
        &:hover {
          background: rgba(64, 169, 255, 0.3);
          transform: scale(1.05);
          box-shadow: 0 0 15px rgba(64, 169, 255, 0.4);
        }
        
        &.playing {
          background: rgba(33, 150, 243, 0.6);
          border-color: rgba(33, 150, 243, 0.8);
          
          &:hover {
            background: rgba(33, 150, 243, 0.7);
          }
        }
        
        &.preparing {
          background: linear-gradient(145deg, #ff9800, #f57c00);
          border-color: rgba(255, 193, 7, 0.6);
          
          &:hover {
            background: linear-gradient(145deg, #ffa726, #fb8c00);
          }
        }
        
        &.checking {
          background: rgba(255, 152, 0, 0.6);
          border-color: rgba(255, 193, 7, 0.6);
          cursor: wait;
        }
        
        .play-icon {
          font-size: 18px;
          color: #fff;
        }
        
        .loading-animation {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 3px;
          
          span {
            display: inline-block;
            width: 4px;
            height: 4px;
            background: #fff;
            border-radius: 50%;
            animation: pulse 1.5s ease-in-out infinite;
            
            &:nth-child(1) { animation-delay: 0s; }
            &:nth-child(2) { animation-delay: 0.5s; }
            &:nth-child(3) { animation-delay: 1s; }
          }
        }
      }
    }
  }
}

.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  
  .empty-icon {
    font-size: 64px;
    color: rgba(255, 255, 255, 0.3);
    margin-bottom: 16px;
  }
  
  .empty-text {
    font-size: 18px;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 8px;
    font-weight: 500;
  }
  
  .empty-description {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.5);
    line-height: 1.4;
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(0.8);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
}
</style>